export const FORFEO_RESERVE_MERCHANT_PORTAL_SCHEMA =
  'forfeo_reserve_merchant_portal_v1' as const;

export const FORFEO_RESERVE_MERCHANT_PORTAL_ACTIONS = [
  'snapshot',
  'command',
  'export',
] as const;

export const FORFEO_RESERVE_MERCHANT_PORTAL_COMMANDS = [
  'upsert_customer_profile',
  'acknowledge_reconciliation_case',
  'resolve_reconciliation_case',
] as const;

export const FORFEO_RESERVE_MERCHANT_PORTAL_EXPORTS = [
  'bookings',
  'customers',
  'reconciliation',
] as const;

export const FORFEO_RESERVE_MERCHANT_PORTAL_AUTHORITIES = {
  discovery: 'EXPLORE',
  merchantUi: 'EXPLORE',
  availability: 'RESERVE',
  calendars: 'RESERVE',
  resources: 'RESERVE',
  employees: 'RESERVE',
  bookings: 'RESERVE',
  customerOperations: 'RESERVE',
  reconciliationWorkflow: 'RESERVE',
  prices: 'GIFTSHOP',
  taxes: 'GIFTSHOP',
  fees: 'GIFTSHOP',
  payments: 'GIFTSHOP',
  refunds: 'GIFTSHOP',
  revenue: 'GIFTSHOP',
} as const;

export type ForfeoReserveMerchantPortalAction =
  typeof FORFEO_RESERVE_MERCHANT_PORTAL_ACTIONS[number];

export type ForfeoReserveMerchantPortalCommandKind =
  typeof FORFEO_RESERVE_MERCHANT_PORTAL_COMMANDS[number];

export type ForfeoReserveMerchantPortalExportKind =
  typeof FORFEO_RESERVE_MERCHANT_PORTAL_EXPORTS[number];

export interface ForfeoReserveMerchantPortalWindowRequest {
  businessId: string;
  startsAt: string;
  endsAt: string;
  pageSize?: number;
  correlationId: string;
}

export interface ForfeoReserveMerchantFinancialReadRequest
  extends ForfeoReserveMerchantPortalWindowRequest {
  schema: typeof FORFEO_RESERVE_MERCHANT_PORTAL_SCHEMA;
}

export interface ForfeoReserveMerchantCustomerProfileInput {
  customerReference: string;
  displayName: string;
  locale: 'fr-CA' | 'en-CA' | 'fr' | 'en';
  preferenceTags: string[];
  operationalNote?: string;
}

export interface ForfeoReserveMerchantPortalCommand {
  kind: ForfeoReserveMerchantPortalCommandKind;
  businessId: string;
  idempotencyKey: string;
  correlationId: string;
  expectedRevision?: number;
  customerProfile?: ForfeoReserveMerchantCustomerProfileInput;
  reconciliationCaseId?: string;
}

export interface ForfeoReserveMerchantPortalExportRequest
  extends ForfeoReserveMerchantPortalWindowRequest {
  exportKind: ForfeoReserveMerchantPortalExportKind;
  rowLimit?: number;
}

export interface ForfeoReserveMerchantPortalMetrics {
  catalogCount: number;
  resourceCount: number;
  employeeCount: number;
  activeClosureCount: number;
  bookingCount: number;
  confirmedBookingCount: number;
  cancelledBookingCount: number;
  noShowBookingCount: number;
  occupiedPartyUnits: number;
  activeCapacityUnits: number;
  operationalOccupancyPercent: number;
  waitlistCount: number;
  openReconciliationCount: number;
}

export interface ForfeoReserveMerchantPortalCatalogItem {
  id: string;
  name: string;
  resourceKind: string;
  timezone: string;
  status: string;
  minimumPartySize: number;
  maximumPartySize: number;
}

export interface ForfeoReserveMerchantPortalResource {
  id: string;
  name: string;
  resourceKind: string;
  capacityTotal: number;
  capacityUnit: string;
  status: string;
  timezone: string;
}

export interface ForfeoReserveMerchantPortalClosure {
  id: string;
  targetType: 'catalog_item' | 'resource';
  targetId: string;
  startsAt: string;
  endsAt: string;
  reasonCode?: string;
}

export interface ForfeoReserveMerchantPortalBooking {
  id: string;
  catalogItemId: string;
  catalogItemName: string;
  customerReference?: string;
  customerLabel: string;
  startsAt: string;
  endsAt: string;
  partySize: number;
  state: string;
  revision: number;
  createdAt: string;
}

export interface ForfeoReserveMerchantPortalCustomerProfile {
  id: string;
  customerReferenceHash: string;
  displayName: string;
  locale: string;
  preferenceTags: string[];
  operationalNote?: string;
  revision: number;
  updatedAt: string;
}

export interface ForfeoReserveMerchantPortalReconciliationCase {
  id: string;
  intentId: string;
  bookingId?: string;
  reason: string;
  state: string;
  createdAt: string;
  resolvedAt?: string;
}

export interface ForfeoReserveMerchantPortalFinancialCurrencyTotal {
  currency: string;
  confirmedCents: number;
  partiallyRefundedCents: number;
  refundedCents: number;
  confirmedCount: number;
  partiallyRefundedCount: number;
  refundedCount: number;
}

export interface ForfeoReserveMerchantPortalFinancialBinding {
  bindingId: string;
  intentId: string;
  confirmationMode: string;
  state: string;
  totalCents: number;
  currency: string;
  createdAt: string;
  confirmedAt?: string;
  closedAt?: string;
}

export interface ForfeoReserveMerchantPortalFinancialEvent {
  eventId: string;
  bindingId: string;
  eventKind: string;
  status: string;
  occurredAt: string;
}

export interface ForfeoReserveMerchantPortalFinancialRead {
  available: boolean;
  authority: 'GIFTSHOP';
  generatedAt?: string;
  currencyTotals: ForfeoReserveMerchantPortalFinancialCurrencyTotal[];
  bindings: ForfeoReserveMerchantPortalFinancialBinding[];
  events: ForfeoReserveMerchantPortalFinancialEvent[];
  errorCode?: string;
}

export interface ForfeoReserveMerchantPortalSnapshot {
  schema: typeof FORFEO_RESERVE_MERCHANT_PORTAL_SCHEMA;
  authority: 'RESERVE';
  businessId: string;
  startsAt: string;
  endsAt: string;
  metrics: ForfeoReserveMerchantPortalMetrics;
  catalog: ForfeoReserveMerchantPortalCatalogItem[];
  resources: ForfeoReserveMerchantPortalResource[];
  closures: ForfeoReserveMerchantPortalClosure[];
  bookings: ForfeoReserveMerchantPortalBooking[];
  waitlist: Record<string, unknown>[];
  customerProfiles: ForfeoReserveMerchantPortalCustomerProfile[];
  reconciliationCases: ForfeoReserveMerchantPortalReconciliationCase[];
  financial: ForfeoReserveMerchantPortalFinancialRead;
  generatedAt: string;
}

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const IDEMPOTENCY = /^[A-Za-z0-9][A-Za-z0-9:._/-]{15,199}$/;
const TAG = /^[\p{L}\p{N}][\p{L}\p{N} _.'’&+/-]{0,49}$/u;
const SAFE_REFERENCE = /^[A-Za-z0-9][A-Za-z0-9:._/-]{7,199}$/;
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE = /(?:\+?\d[\s().-]*){8,}/;
const CARD = /\b(?:\d[ -]*?){13,19}\b/;
const FORBIDDEN_CLIENT_KEYS = new Set([
  'amount',
  'amount_cents',
  'price',
  'price_cents',
  'subtotal',
  'subtotal_cents',
  'total',
  'total_cents',
  'tax',
  'tax_cents',
  'fee',
  'fee_cents',
  'deposit',
  'deposit_cents',
  'currency',
  'stripe_session_id',
  'stripe_payment_intent_id',
  'stripe_refund_id',
  'stripe_customer_id',
  'gross_amount_cents',
  'net_amount_cents',
  'refund_amount_cents',
  'amountcents',
  'pricecents',
  'subtotalcents',
  'totalcents',
  'taxcents',
  'feecents',
  'depositcents',
  'stripesessionid',
  'stripepaymentintentid',
  'striperefundid',
  'stripecustomerid',
  'grossamountcents',
  'netamountcents',
  'refundamountcents',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validIso(value: unknown): boolean {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

export function isForfeoReserveMerchantPortalAction(
  value: unknown,
): value is ForfeoReserveMerchantPortalAction {
  return FORFEO_RESERVE_MERCHANT_PORTAL_ACTIONS.includes(
    value as ForfeoReserveMerchantPortalAction,
  );
}

export function isForfeoReserveMerchantPortalCommandKind(
  value: unknown,
): value is ForfeoReserveMerchantPortalCommandKind {
  return FORFEO_RESERVE_MERCHANT_PORTAL_COMMANDS.includes(
    value as ForfeoReserveMerchantPortalCommandKind,
  );
}

export function isForfeoReserveMerchantPortalExportKind(
  value: unknown,
): value is ForfeoReserveMerchantPortalExportKind {
  return FORFEO_RESERVE_MERCHANT_PORTAL_EXPORTS.includes(
    value as ForfeoReserveMerchantPortalExportKind,
  );
}

export function normalizeForfeoReserveMerchantPortalLimit(
  value: unknown,
  maximum = 100,
): number {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed)) return 50;
  return Math.max(1, Math.min(maximum, parsed));
}

export function hasForfeoReserveMerchantPortalForbiddenFinancialFields(
  value: unknown,
): boolean {
  if (Array.isArray(value)) {
    return value.some(hasForfeoReserveMerchantPortalForbiddenFinancialFields);
  }
  if (!isRecord(value)) return false;
  for (const [key, nested] of Object.entries(value)) {
    if (FORBIDDEN_CLIENT_KEYS.has(key.toLowerCase())) return true;
    if (hasForfeoReserveMerchantPortalForbiddenFinancialFields(nested)) {
      return true;
    }
  }
  return false;
}

export function validateForfeoReserveMerchantPortalWindowRequest(
  request: ForfeoReserveMerchantPortalWindowRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!isRecord(request)) return ['invalid_window_request'];
  if (!UUID.test(String(request.businessId || ''))) errors.push('invalid_business_id');
  if (!validIso(request.startsAt)) errors.push('invalid_starts_at');
  if (!validIso(request.endsAt)) errors.push('invalid_ends_at');
  if (validIso(request.startsAt) && validIso(request.endsAt)) {
    const starts = Date.parse(request.startsAt);
    const ends = Date.parse(request.endsAt);
    if (starts >= ends) errors.push('invalid_window_order');
    if (ends - starts > 366 * 86_400_000) errors.push('window_too_large');
  }
  if (
    request.pageSize !== undefined
    && (!Number.isSafeInteger(request.pageSize)
      || request.pageSize < 1
      || request.pageSize > 100)
  ) errors.push('invalid_page_size');
  if (
    typeof request.correlationId !== 'string'
    || request.correlationId.trim().length < 8
    || request.correlationId.length > 200
  ) errors.push('invalid_correlation_id');
  if (hasForfeoReserveMerchantPortalForbiddenFinancialFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReserveMerchantFinancialReadRequest(
  request: ForfeoReserveMerchantFinancialReadRequest,
): readonly string[] {
  const errors = [...validateForfeoReserveMerchantPortalWindowRequest(request)];
  if (request.schema !== FORFEO_RESERVE_MERCHANT_PORTAL_SCHEMA) {
    errors.push('invalid_schema');
  }
  return errors;
}

export function validateForfeoReserveMerchantCustomerProfile(
  profile: ForfeoReserveMerchantCustomerProfileInput,
): readonly string[] {
  const errors: string[] = [];
  if (!isRecord(profile)) return ['invalid_customer_profile'];
  if (!SAFE_REFERENCE.test(String(profile.customerReference || ''))) {
    errors.push('invalid_customer_reference');
  }
  if (
    typeof profile.displayName !== 'string'
    || profile.displayName.trim().length < 1
    || profile.displayName.trim().length > 160
  ) errors.push('invalid_display_name');
  if (!['fr-CA', 'en-CA', 'fr', 'en'].includes(profile.locale)) {
    errors.push('invalid_locale');
  }
  if (!Array.isArray(profile.preferenceTags) || profile.preferenceTags.length > 20) {
    errors.push('invalid_preference_tags');
  } else if (profile.preferenceTags.some((tag) => !TAG.test(String(tag)))) {
    errors.push('invalid_preference_tag');
  }
  const note = String(profile.operationalNote || '').trim();
  if (note.length > 1000) errors.push('operational_note_too_long');
  if (note && (EMAIL.test(note) || PHONE.test(note) || CARD.test(note))) {
    errors.push('sensitive_note_forbidden');
  }
  return errors;
}

export function validateForfeoReserveMerchantPortalCommand(
  command: ForfeoReserveMerchantPortalCommand,
): readonly string[] {
  const errors: string[] = [];
  if (!isRecord(command)) return ['invalid_portal_command'];
  if (!isForfeoReserveMerchantPortalCommandKind(command.kind)) {
    errors.push('invalid_command_kind');
  }
  if (!UUID.test(String(command.businessId || ''))) errors.push('invalid_business_id');
  if (!IDEMPOTENCY.test(String(command.idempotencyKey || ''))) {
    errors.push('invalid_idempotency_key');
  }
  if (
    typeof command.correlationId !== 'string'
    || command.correlationId.trim().length < 8
    || command.correlationId.length > 200
  ) errors.push('invalid_correlation_id');
  if (
    command.expectedRevision !== undefined
    && (!Number.isSafeInteger(command.expectedRevision)
      || command.expectedRevision < 0)
  ) errors.push('invalid_expected_revision');

  if (command.kind === 'upsert_customer_profile') {
    errors.push(...validateForfeoReserveMerchantCustomerProfile(
      command.customerProfile as ForfeoReserveMerchantCustomerProfileInput,
    ));
  } else if (!UUID.test(String(command.reconciliationCaseId || ''))) {
    errors.push('invalid_reconciliation_case_id');
  }

  if (hasForfeoReserveMerchantPortalForbiddenFinancialFields(command)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReserveMerchantPortalExportRequest(
  request: ForfeoReserveMerchantPortalExportRequest,
): readonly string[] {
  const errors = [...validateForfeoReserveMerchantPortalWindowRequest(request)];
  if (!isForfeoReserveMerchantPortalExportKind(request.exportKind)) {
    errors.push('invalid_export_kind');
  }
  if (
    request.rowLimit !== undefined
    && (!Number.isSafeInteger(request.rowLimit)
      || request.rowLimit < 1
      || request.rowLimit > 500)
  ) errors.push('invalid_row_limit');
  return errors;
}
